import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Palette } from 'lucide-react';
import { brandsApi } from '@/features/brands/api';
import { xamplinApi } from '@/lib/api/xamplin';
import { queryKeys } from '@/lib/api/queryKeys';

/**
 * Brands hub (build-v2 F3) — real brand cards (CoCompany) → Brand Studio.
 * Each card shows the brand's identity (colors/logo) + its xp-project count.
 */
export function BrandsHubPage() {
  const brandsQ = useQuery({
    queryKey: queryKeys.brands.all,
    queryFn: () => brandsApi.list(),
  });
  const projectsQ = useQuery({
    queryKey: queryKeys.xamplin.projects(),
    queryFn: () => xamplinApi.projects.list(),
  });

  const brands = (brandsQ.data?.items ?? []).filter((b) => b.companyType !== 'Agency');
  const projects = projectsQ.data?.items ?? [];
  const countByBrand = useMemo(() => {
    const m = new Map<number, number>();
    for (const p of projects) {
      if (p.companyId != null) m.set(p.companyId, (m.get(p.companyId) ?? 0) + 1);
    }
    return m;
  }, [projects]);

  return (
    <div className="page flex flex-col gap-5">
      <div className="flex items-center gap-3">
        <span className="grid h-10 w-10 place-items-center rounded-xl bg-[var(--color-accent-10)] text-[var(--color-xk-accent)]">
          <Palette className="h-5 w-5" />
        </span>
        <div>
          <h1 className="font-display text-2xl font-extrabold tracking-tight text-[var(--color-xk-lavender)]">
            Brands
          </h1>
          <p className="mono text-xs uppercase tracking-wider text-[var(--color-xk-text-muted)]">
            {brands.length} brands
          </p>
        </div>
      </div>

      {brandsQ.isLoading && (
        <div className="mono text-sm text-[var(--color-xk-text-muted)]">Cargando marcas…</div>
      )}
      {brandsQ.isError && (
        <div className="rounded-xl border border-[var(--color-error)] bg-[var(--color-error)]/10 px-4 py-3 text-sm text-[var(--color-error)]">
          No se pudieron cargar las marcas.
        </div>
      )}

      {!brandsQ.isLoading && !brandsQ.isError && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {brands.map((b) => (
            <Link
              key={b.id}
              to={`/brands/${b.id}`}
              className="group flex items-center gap-3 rounded-2xl border border-[var(--color-xk-border)] bg-[var(--color-xk-surface)] p-4 transition-colors hover:border-[var(--color-xk-accent)]"
            >
              <span
                className="grid h-11 w-11 flex-none place-items-center rounded-xl text-base font-extrabold text-white"
                style={{ background: b.secondaryColor || 'var(--color-xk-accent)' }}
              >
                {b.name.slice(0, 1).toUpperCase()}
              </span>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-bold text-[var(--color-xk-lavender)]">
                  {b.name}
                </div>
                <div className="mono text-[11px] text-[var(--color-xk-text-muted)]">
                  {countByBrand.get(b.id) ?? 0} project
                  {(countByBrand.get(b.id) ?? 0) === 1 ? '' : 's'}
                </div>
              </div>
              <span className="mono text-[11px] text-[var(--color-xk-accent)] opacity-0 transition-opacity group-hover:opacity-100">
                Studio →
              </span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
